import React, { useMemo } from "react";
import { CDataTable } from "@coreui/react";
import ExcelExportButton from "./ExcelExportButton";
import { getBaseUrlParams } from "../../../utils/getBaseUrlParams";
import { linkifyClientIds } from "../../../utils/linkifyClientIds";
import { decodeHtml } from "../utils";

const stripInvalidFileNameChars = (str) =>
  typeof str === "string" ? str.replace(/[\\/:*?"<>|[\]]/g, "").trim() : "";

const renderCellValue = (val) => {
  if (typeof val === "string" && /<\/?[a-z][\s\S]*>/i.test(val)) {
    return <span dangerouslySetInnerHTML={{ __html: val }} />;
  }
  return val;
};

const parseNumeric = (val) => {
  if (typeof val === "number") return val;
  if (typeof val !== "string" || val === "") return NaN;
  const stripped = val.replace(/[$,%\s]/g, "");
  return stripped === "" ? NaN : Number(stripped);
};

const USER_ID_COLUMN_HEADER_RE = /(user|member|client|customer)\s*(id|uid)$|^(id|uid)$/i;
const BARE_USER_ID_VALUE_RE = /^\d{8,9}$/;

const TableBlock = ({ table }) => {
  const { title, columns = [], rows = [] } = table || {};
  const { organization_id, short_url, object_id } = getBaseUrlParams();

  const fields = useMemo(() => columns.map((col) => ({ key: col, label: decodeHtml(col) })), [columns]);

  const items = useMemo(
    () =>
      rows.map((row) => {
        const item = { _display: {} };
        columns.forEach((col, i) => {
          const val = row[i] ?? "";
          const num = parseNumeric(val);
          if (!isNaN(num)) {
            item[col] = num;
            if (String(num) !== String(val)) {
              item._display[col] = val;
            }
          } else {
            item[col] = val ? decodeHtml(val) : "";
          }
        });
        return item;
      }),
    [columns, rows]
  );

  const scopedSlots = useMemo(() => {
    const slots = {};
    const wholeCellRe = organization_id ? linkifyClientIds.COMPOUND_ID_TEST_RE : null;
    const colIndex = {};
    columns.forEach((col, i) => { colIndex[col] = i; });

    const cellContainsCompoundId = (val) => {
      const re = /\b\d{8,9}_\d+\b/;
      return re.test(val);
    };

    columns.forEach((col) => {
      // Client ID links (highest priority): cell is exactly a compound ID, or contains one inline
      if (wholeCellRe && rows.some((row) => cellContainsCompoundId(String(row[colIndex[col]] ?? "")))) {
        slots[col] = (item) => {
          const val = String(item[col] ?? "");
          if (wholeCellRe.test(val)) {
            const { clientUid, objectId: storeDbId } = linkifyClientIds.parseCompoundId(val);
            const url = linkifyClientIds.href(organization_id, clientUid, short_url, storeDbId);
            return (
              <td style={{ whiteSpace: "nowrap" }}>
                <a href={url} target="_client_profile">
                  {clientUid}
                </a>
              </td>
            );
          }
          // Inline: split on each compound ID; emit anchors for matches, text for the rest.
          // Use a fresh regex (no shared lastIndex) with two capture groups so split keeps clientUid and storeDbId.
          const parts = val.split(/\b(\d{8,9})_(\d+)\b/g);
          if (parts.length === 1) return <td>{val}</td>;
          const children = [];
          for (let i = 0; i < parts.length; i += 3) {
            if (parts[i]) children.push(parts[i]);
            if (i + 2 < parts.length) {
              const clientUid = parts[i + 1];
              const storeDbId = parts[i + 2];
              const url = linkifyClientIds.href(organization_id, clientUid, short_url, storeDbId);
              children.push(
                <a key={`cu-${i}`} href={url} target="_client_profile">
                  {clientUid}
                </a>
              );
            }
          }
          return <td>{children}</td>;
        };
        return;
      }

      // Bare user_id column: header is "User ID" / "Member ID" / "ID" etc. AND cells are bare 8-9 digit numbers.
      // Render each as a link using the current store's object_id from URL params.
      const headerStr = String(col || "").trim();
      if (
        organization_id &&
        object_id &&
        USER_ID_COLUMN_HEADER_RE.test(headerStr) &&
        rows.some((row) => BARE_USER_ID_VALUE_RE.test(String(row[colIndex[col]] ?? "")))
      ) {
        slots[col] = (item) => {
          const val = String(item[col] ?? "");
          if (!BARE_USER_ID_VALUE_RE.test(val)) return <td>{item[col]}</td>;
          const url = linkifyClientIds.href(organization_id, val, short_url, object_id);
          return (
            <td style={{ whiteSpace: "nowrap" }}>
              <a href={url} target="_client_profile">
                {val}
              </a>
            </td>
          );
        };
        return;
      }

      // Display formatting (numeric with original format) or HTML rendering
      const needsFormat = items.some((item) => item._display[col] !== undefined);
      const hasHtml = !needsFormat && items.some((item) => {
        const val = item._display[col] ?? item[col];
        return typeof val === "string" && /<\/?[a-z][\s\S]*>/i.test(val);
      });

      if (needsFormat || hasHtml) {
        slots[col] = (item) => <td>{renderCellValue(item._display[col] ?? item[col])}</td>;
      }
    });

    return Object.keys(slots).length ? slots : undefined;
  }, [columns, rows, organization_id, short_url, object_id, items]);

  const exportRows = () => {
    const wholeCellRe = linkifyClientIds.COMPOUND_ID_TEST_RE;
    return rows.map((row) =>
      row.map((cell) => {
        const val = String(cell ?? "");
        if (wholeCellRe.test(val)) {
          return linkifyClientIds.parseCompoundId(val).clientUid;
        }
        const stripped = val.replace(/\b(\d{8,9})_(\d+)\b/g, "$1");
        return stripped === val ? cell : stripped;
      })
    );
  };

  if (!columns.length || !rows.length) return null;

  return (
    <div className="table-block">
      {title && <div className="table-block__title">{decodeHtml(title)}</div>}
      <CDataTable items={items} fields={fields} scopedSlots={scopedSlots} sorter dark hover />
      <ExcelExportButton
        headers={columns}
        data={exportRows()}
        defaultName={stripInvalidFileNameChars(title) || "table-data"}
      />
    </div>
  );
};

export default TableBlock;
